import React, { useState, useEffect } from 'react'
import { OptionsHOC } from '../../hocs'
import { Image } from 'react-bootstrap'
import { Motion, spring } from 'react-motion'
import Velocity from 'velocity-animate'
import PropTypes from 'prop-types'
import 'velocity-animate/velocity.ui'
import { hexToRgbA } from '../../helpers/utils'

const InteractiveDevice = props => {
  const [ phoneImageLoaded, setPhoneImageLoaded ] = useState(false)
  const [ phoneHeight, setPhoneHeight ] = useState(0)
  const [ phoneWidth, setPhoneWidth ] = useState(0)
  const [ swipeTimer, setSwipeTimer ] = useState(null)

  useEffect(() => {
    toggleSwipeAnimation()
    toggleStepAnimation()
  }, [props.stepData])

  const toggleSwipeAnimation = () => {
    const { stepData: { id, button, phoneImage, portrait }} = props
    if (id) {
      stopSwipeAnimation()
      playSwipeAnimation(button, phoneImage, portrait)
    }
  }

  const toggleStepAnimation = () => {
    const { currentStep, playStepAnimation, automatedStep } = props
    if (currentStep === automatedStep()) {
      playStepAnimation()
    }
  }

  const shouldDisplayAnimation = () => (
    !(props.currentStep === props.automatedStep())
  )

  const handlePhoneLoad = e => {
    setPhoneHeight(e.target.naturalHeight)
    setPhoneWidth(e.target.naturalWidth)
    setPhoneImageLoaded(true)
  }

  const calcButtonPosition = (button, phoneImage, portrait) => {
    let top, left, centerY, centerX

    // button not defined
    if (!button) {
      top = 0
      left = 0
      centerY = 0
      centerX = 0
      // this is a physical button -- don't add the screen offset
    } else if (button.lcdButton === 0 && !isSwipe(button)) {
      top = button.yUpperLeft
      left = button.xUpperLeft
    } else {
      top = button.yUpperLeft + phoneImage.original.screenYUpperLeft //  + dY)
      left = button.xUpperLeft + phoneImage.original.screenXUpperLeft // + dX)
    }

    // now we have top and left upper corners but need to center the button
    // take the corner and add half the box size and subtract half the radius of the circle

    if (button) {
      centerY = top + button.ySize / 2 - 16
      centerX = left + button.xSize / 2 - 16

      // flip the x and y if the phone is NOT rotated in responsive mode but it would be in regular mode
      // the screen is prevented from rotating but button position still acts as if its horizontal
      const mediaQuery = window.matchMedia('(min-width: 550px)')
      if (!mediaQuery.matches && !portrait) {
        let temp = centerY
        centerY = centerX
        centerX = temp
      }
    }

    // return an object
    return {
      top: centerY,
      left: centerX
    }
  }

  /* does this button contain a swipe?
   * returns a boolean
   */
  const isSwipe = button => {
    const swipeOrAction =
      button && button.swipeOrAction ? button.swipeOrAction : null
    let res = false
    switch (swipeOrAction) {
      case 'UP':
      case 'DOWN':
      case 'LEFT':
      case 'RIGHT':
        res = true
        break
      default:
        break
    }

    return res
  }

  /* create a styling around the swipe animation to contain it and set the width
   *  this current its set only for a horizontal swipe positoned 180px down the screen
   *  @todo: It needs adjusted to be positoned at the button location instead
   *  This should probably handle all buttons not just the swipe, right now
   *  the logic is a bit goofy how it switches between button and container
   */

  const getTouchContainerStyle = (button, buttonX, buttonY) => {
    const showTouchContainer = phoneImageLoaded && button.xSize && button.ySize
    const rotation = getTouchContainerRotation(button)

    return {
      cursor: 'pointer',
      width: 32,
      height: 32,
      bottom: false,
      left: false,
      position: 'absolute',
      zIndex: 45,
      msTransformOrigin: '50% 50%',
      transformOrigin: '50% 50%',
      display: showTouchContainer ? 'block' : 'none',
      WebkitTransform: `translate3d(${buttonX}px, ${buttonY}px, 0) rotate(${rotation}deg) `,
      transform: `translate3d(${buttonX}px, ${buttonY}px, 0) rotate(${rotation}deg) `
    }
  }

  // Get rotation value of swipe container
  const getTouchContainerRotation = button => {
    const swipeOrAction =
      button && button.swipeOrAction ? button.swipeOrAction : null
    let rotation = 0
    switch (swipeOrAction) {
      case 'DOWN':
        rotation = 180
        break
      case 'LEFT':
        rotation = -90
        break
      case 'RIGHT':
        rotation = 90
        break
      default:
        break
    }

    return rotation
  }

  /* playSwipeAnimation is called once each step is loaded ...
   * this is a separate animation that should happen after the dot moves into position
   * The only way to chain together another animation was to go around the abstraction and
   * add another animation with a loop afterward.  This is meant to represent a swiping action
   */
  const playSwipeAnimation = btn => {
    // This number is used on backend - when button is set as swipe
    // it's position gets moved 75 points to the oppossite side of animation
    const animationTrailLength = 75

    // the button element in the dom
    const button = document.getElementById('touch')

    let startLeft,
      endLeft,
      startTop,
      endTop = 0
    let trailClass, trailShadow
    const c = hexToRgbA(props.getOption('theme.accentColor', '#62bb46'), 0.8)

    if (btn && btn.swipeOrAction) {
      startTop = -animationTrailLength
      trailClass = 'trail'
      trailShadow = `0px 10px 20px 0px ${c}, 0 30px 20px 0px ${c},  0 50px 20px 0px ${c}, 0 70px 20px 0px ${c}`
    }

    if (btn && ['LEFT', 'RIGHT', 'DOWN', 'UP'].includes(btn.swipeOrAction)) {
      // velocityjs forcefeed start values because apparently we can't get the current positioning from the DOM
      let swipeTimer = setInterval(function() {
        Velocity(
          button,
          {
            translateX: [startLeft, endLeft],
            translateY: [startTop, endTop]
          },
          {
            easing: 'ease-in-out',
            duration: 850,
            begin: function(el) {
              el[0].style.transform = ''
              if (trailShadow !== '') el[0].style.boxShadow = trailShadow
              el[0].classList.add(trailClass)
            },
            complete: function(el) {
              el[0].style.transform = ''
              if (trailShadow !== '') el[0].style.boxShadow = ''
              el[0].classList.remove(trailClass)
            }
          }
        )
      }, 1300)
      // add the timer to the react state so it can be cleared later
      setSwipeTimer(swipeTimer)
    }
  }

  /* stop the currently playing animation */
  const stopSwipeAnimation = () => {
    const button = document.getElementById('touch')
    if (button) {
      button.style.transform = ''
      button.top = 0
      button.left = 0
      button.style.boxShadow = ''
      button.classList.remove('trail')
    }

    clearInterval(swipeTimer)
  }

  const handleOnPhoneClick = () => {
    const {
      stepData,
      playStepAnimation,
      createUserEvent,
      updateStep,
      windowDimensions,
      currentPhoneId
    } = props

    if (stepData.isPlaying) {
      return null
    }

    createUserEvent({
      category: 'tutorials',
      data: {
        action: 'OnDeviceClick',
        target: stepData.id,
        step_to: stepData.steps.currentId + 1,
        device_id: currentPhoneId,
      }
    })
    windowDimensions.width > 700
      ? playStepAnimation()
      : updateStep(stepData.steps.nextStepId)
  }

  const getPhoneImageContainerStyle = (rotate, x, y) => {
    const { stepData } = props
    const marginBottom = stepData.portrait ? 0 : phoneWidth - phoneHeight
    return {
      width: phoneWidth,
      float: 'left',
      marginBottom,
      WebkitTransform: `rotate(${rotate}deg) translate3d(${x}px, ${y}px, 0)`,
      transform: `rotate(${rotate}deg) translate3d(${x}px, ${y}px, 0)`
    }
  }

  const { stepData, windowDimensions } = props

  const phoneOffset =
    stepData.portrait || windowDimensions.width <= 700
      ? 0
      : (phoneHeight - phoneWidth) / 2
  const lcdStyle = {
    top: stepData.phoneImage.screenYUpperLeft - phoneHeight,
    left: stepData.phoneImage.screenXUpperLeft,
    width: stepData.phoneImage.screenXSize,
    height: stepData.phoneImage.screenYSize,
    marginBottom:
      stepData.phoneImage.screenYUpperLeft - phoneHeight,
    display: 'block',
    zIndex: stepData.phoneImage.faceplate_behind === 1 ? 3 : 1

  }
  let buttonPosition = calcButtonPosition(
    stepData.button,
    stepData.phoneImage,
    stepData.portrait
  )

  return (
    <div className="flex">
      <Motion
        style={{
          rotate: spring(
            stepData.portrait || windowDimensions.width <= 700 ? 0 : -90
          ),
          x: spring(phoneOffset),
          y: spring(phoneOffset)
        }}
      >
        {({ rotate, x, y }) => (
          <div
            style={{ width: phoneWidth, position: 'relative' }}
            className="phone-container"
          >
            <div
              className="phone-image-container"
              style={getPhoneImageContainerStyle(rotate, x, y)}
            >
              <Image
                style={{ position: 'relative', zIndex: 2}}
                id="phone-image"
                ref="phoneImage"
                alt={props.deviceName || ''}
                onLoad={handlePhoneLoad}
                src={stepData.phoneImage.imageLocation}
              />
              <If condition={phoneImageLoaded && stepData.lcdImage}>
                <Image
                  style={lcdStyle}
                  className="lcd-image"
                  responsive
                  alt={props.deviceName || ''}
                  src={stepData.lcdImage}
                />
              </If>
            </div>
            <If
              condition={
                buttonPosition.left >= -25 && buttonPosition.top >= -25
              }
            >
              <If condition={shouldDisplayAnimation()}>
                <Motion
                  style={{
                    buttonX: spring(buttonPosition.left),
                    buttonY: spring(buttonPosition.top)
                  }}
                >
                  {({ buttonX, buttonY }) => (
                    <div
                      id="touch-container"
                      style={
                        getTouchContainerStyle(stepData.button, buttonX, buttonY)
                      }
                      onClick={handleOnPhoneClick}
                    >
                      <button
                        id="touch"
                        className="touch"
                        style={{ backgroundColor: hexToRgbA(props.getOption('theme.accentColor', '#62bb46'), 0.8)}}
                      >
                        Press to advance tutorial
                      </button>
                    </div>
                  )}
                </Motion>
              </If>
            </If>
          </div>
        )}
      </Motion>
    </div>
  )
}

InteractiveDevice.propTypes = {
  currentStep: PropTypes.number.isRequired,
  playStepAnimation: PropTypes.func.isRequired,
  automatedStep: PropTypes.func.isRequired,
  createUserEvent: PropTypes.func.isRequired,
  updateStep: PropTypes.func.isRequired,
  deviceName: PropTypes.string.isRequired,
  windowDimensions: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }).isRequired,
  stepData: PropTypes.shape({
    id: PropTypes.number.isRequired,
    portrait: PropTypes.bool.isRequired,
    isPlaying: PropTypes.bool.isRequired,
    lcdImage: PropTypes.string.isRequired,

    button: PropTypes.shape({
      lcdButton: PropTypes.number.isRequired,
      swipeOrAction: PropTypes.string,
      xSize: PropTypes.number.isRequired,
      ySize: PropTypes.number.isRequired,
      xUpperLeft: PropTypes.number.isRequired,
      yUpperLeft: PropTypes.number.isRequired,
    }).isRequired,

    phoneImage: PropTypes.shape({
      imageLocation: PropTypes.string.isRequired,
      screenXSize: PropTypes.number.isRequired,
      screenYSize: PropTypes.number.isRequired,
      screenXUpperLeft: PropTypes.number.isRequired,
      screenYUpperLeft: PropTypes.number.isRequired,
      original: PropTypes.shape({
        screenXUpperLeft: PropTypes.number.isRequired,
        screenYUpperLeft: PropTypes.number.isRequired,
      }).isRequired
    }).isRequired,

    steps: PropTypes.shape({
      currentId: PropTypes.number.isRequired,
      nextStepId: PropTypes.number.isRequired,
    }).isRequired
  }).isRequired,
  currentPhoneId: PropTypes.number.isRequired
}

export default OptionsHOC(InteractiveDevice)



// WEBPACK FOOTER //
// ./src/components/InteractiveDevice/InteractiveDevice.js